import React from 'react';
import { ArrowLeft, ArrowRight, ShieldAlert, BookOpen } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

const bgImage = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/nuly59ss_HELP%20CENTRE.jpg';

const HelpCentre = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromTopNav = location.state?.from === 'top-nav';

  return (
    <div className="min-h-screen flex flex-col" data-testid="help-centre-page">
      <Header />

      <div className="h-20" />

      {/* Full-page background image */}
      <div className="relative flex-1">
        <div
          className="fixed inset-0 top-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="fixed inset-0 top-20 bg-white/0" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <button
            onClick={() => (fromTopNav ? navigate('/') : navigate(-1))}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid="help-centre-back-button"
          >
            <ArrowLeft size={18} />
            {fromTopNav ? 'Back to Home' : 'Back'}
          </button>

          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-8" data-testid="help-centre-title">
              Help Centre
            </h1>

            <div className="space-y-5 text-gray-700 text-base sm:text-lg leading-relaxed">
              <p>
                At eCOMM, we understand that navigating payment processing can feel complex. Our Help Centre is your one-stop destination for answers, guidance and support. Whether you're a new merchant setting up your first payment solution or an experienced business looking to optimise your operations, we're here to ensure your success every step of the way.
              </p>
              <p>
                Our Help Centre is designed to empower you with the knowledge and tools you need to make the most of your payment solutions. From understanding payment terminology to protecting your business from fraud, we've gathered essential resources to support your growth.
              </p>
            </div>
          </div>

          {/* Resource Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
            <Link
              to="/fraud-prevention"
              className="group bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 hover:shadow-xl hover:bg-white/90 transition-all duration-300"
              data-testid="help-centre-fraud-link"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#1F4E79] transition-colors duration-200 flex items-center gap-2">
                <ShieldAlert size={24} className="text-[#1F4E79] flex-shrink-0" />
                Fraud & Risk Prevention
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                In today's digital landscape, security is essential. Our Fraud & Risk prevention resources equip you with the knowledge and tools to safeguard your business, protect your customers and maintain trust in every transaction. Read about our latest fraud prevention strategies, risk assessment guidance and security best practices.
              </p>
              <span className="inline-flex items-center gap-1 text-[#1F4E79] font-semibold text-sm group-hover:gap-2 transition-all duration-200">
                Learn more <ArrowRight size={16} />
              </span>
            </Link>

            <Link
              to="/glossary"
              className="group bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 hover:shadow-xl hover:bg-white/90 transition-all duration-300"
              data-testid="help-centre-glossary-link"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#1F4E79] transition-colors duration-200 flex items-center gap-2">
                <BookOpen size={24} className="text-[#1F4E79] flex-shrink-0" />
                Glossary
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Understanding payment processing doesn't have to be complicated. Our comprehensive glossary breaks down industry jargon into clear, simple explanations. Whether you're wondering about interchange rates, authorisation codes or settlement periods, find instant clarity on the terms that matter to your business.
              </p>
              <span className="inline-flex items-center gap-1 text-[#1F4E79] font-semibold text-sm group-hover:gap-2 transition-all duration-200">
                Learn more <ArrowRight size={16} />
              </span>
            </Link>
          </div>

          {/* Need More Help */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="help-centre-need-help">
              Need More Help?
            </h2>
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-8">
              Can't find what you're looking for? Our dedicated support team is ready to assist you with personalised guidance tailored to your specific needs.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mb-5">Get in Touch:</h3>

            <div className="space-y-4">
              <div>
                <p className="font-semibold text-gray-900">Phone Support</p>
                <p className="text-gray-700">EU: +353 (0)46 901 1288</p>
                <p className="text-gray-700">UK: +44 (0)20 7183 5253</p>
              </div>

              <div>
                <p className="font-semibold text-gray-900">Email</p>
                <a
                  href="mailto:csr@ecomm365.com"
                  className="text-[#1F4E79] hover:text-[#163a5f] underline transition-colors duration-200"
                  data-testid="help-centre-email-link"
                >
                  csr@ecomm365.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
      <BackToTop />
    </div>
  );
};

export default HelpCentre;
